import type { OnboardingStepProps } from "./Onboarding";
import type { OnboardingDesignLibraryContentData } from "./OnboardingDesignLibraryContent";

import React, { useRef } from "react";

import useCurrentUser from "@editor/hooks/useCurrentUser";
import { useLocalStorageState } from "@editor/hooks/useLocalStorage";
import { useLogAnalytics } from "@editor/hooks/useLogAnalytics";

import { useNavigate } from "react-router-dom";
import { z } from "zod";

import OnboardingDesignLibraryContent from "./OnboardingDesignLibraryContent";
import OnboardingFooter from "./OnboardingFooter";

const OnboardingDesignLibrary: React.FC<OnboardingStepProps> = ({
  currentStep,
  submitStep,
  isLoading,
  totalSteps,
}) => {
  const navigate = useNavigate();
  const logEvent = useLogAnalytics();
  const { user } = useCurrentUser();
  const onboardingDesignLibraryContentRef = useRef<{
    getData: () => OnboardingDesignLibraryContentData;
  }>(null);

  const [selectedOption] = useLocalStorageState(
    "onboarding-setup-selected-option",
    null,
    {
      schema: z.union([z.literal("new"), z.literal("import"), z.null()]),
    },
  );

  const handleSubmit = async () => {
    if (!onboardingDesignLibraryContentRef.current) {
      submitStep();
      return;
    }

    const data = onboardingDesignLibraryContentRef.current.getData();
    const {
      styleLibrary: styleLibraryToSave,
      selectedTheme,
      extractedData,
      hasCompletedManualExtraction,
      hasValidColors,
      hasValidTypography,
      updateStyleLibrary,
      projectId,
    } = data;

    if (styleLibraryToSave && projectId) {
      const hasColors = hasValidColors(styleLibraryToSave.colors);
      const hasTypography = hasValidTypography(styleLibraryToSave.typography);

      if (hasColors || hasTypography) {
        try {
          await updateStyleLibrary({
            projectId,
            styleLibrary: styleLibraryToSave,
          });
        } catch (error) {
          console.error("Error updating style library:", error);
        }
      }
    }

    if (user) {
      let extractionMethod = "theme";
      if (selectedOption === "import") {
        extractionMethod = "shopify";
      } else if (hasCompletedManualExtraction) {
        extractionMethod = "manual";
      }

      logEvent("onboarding.flow.step.completed", {
        userId: user.email,
        stepId: "design-setup",
        stepType: "design-setup",
        stepData: {
          selectedTheme,
          hasExtractedStyles: Boolean(extractedData),
          hasCompletedManualExtraction,
          extractionMethod,
          styleLibrary: styleLibraryToSave
            ? {
                hasColors: hasValidColors(styleLibraryToSave.colors),
                hasTypography: hasValidTypography(
                  styleLibraryToSave.typography,
                ),
                extractedFontsCount:
                  extractedData?.styles.uploadedFonts?.length ?? 0,
                extractedColorsCount: extractedData?.styles.colors?.length ?? 0,
              }
            : null,
        },
      });
    }

    submitStep();
  };

  const handleBack = () => {
    navigate(`/onboarding/onboarding-setup-shop`);
  };

  return (
    <div className="h-screen w-full flex flex-col">
      <OnboardingDesignLibraryContent ref={onboardingDesignLibraryContentRef} />

      <div className="absolute right-0 bottom-0 left-0 z-20">
        <OnboardingFooter
          currentStep={currentStep}
          onNext={() => void handleSubmit()}
          onBack={handleBack}
          className="w-full md:max-w-[1400px]"
          isNextLoading={isLoading}
          totalSteps={totalSteps}
        />
      </div>
    </div>
  );
};

export default OnboardingDesignLibrary;
